import { useEffect, useState } from 'react';

/*
  Streams /api/search/stream (server/routes/search.js) for the palette: NDJSON events (meta, group, done) arrive as each
  provider finishes, so the first groups show within ~100 ms while lines, notes and files are still being searched.
  Typing on aborts the running request (the server then stops starting lookups). The previous results stay on screen,
  dimmed, until the first group of the new query arrives, so the list never flashes empty. Finished searches are kept
  for a minute: backspacing to an earlier query is instant.
*/

export const EMPTY_SEARCH = Object.freeze({ status: 'idle', text: '', query: null, order: [], groups: [], results: 0, ms: null, stale: false, deepPlanned: false });

const CACHE_TTL_MS = 60 * 1000;
const CACHE_SIZE = 40;
const FLUSH_MS = 40;
const cache = new Map();

function cacheGet(key) {
  const hit = cache.get(key);
  if (!hit || Date.now() - hit.at > CACHE_TTL_MS) return null;
  return hit.state;
}

function cachePut(key, state) {
  cache.delete(key);
  cache.set(key, { at: Date.now(), state });
  while (cache.size > CACHE_SIZE) cache.delete(cache.keys().next().value);
}

const byDateDesc = (a, b) => String(b.date || '').localeCompare(String(a.date || ''));

// one group per key: sources feeding the same group (lines of invoices, of quotations, ...) are merged newest first
function addGroup(state, event) {
  const current = state.groups.find((group) => group.key === event.key);
  const merged = current
    ? {
        ...current,
        items: [...current.items, ...event.items].sort(byDateDesc),
        hasMore: current.hasMore || event.hasMore,
        sources: current.sources + 1,
        timedOut: current.timedOut || event.error === 'timeout'
      }
    : { key: event.key, label: event.label, phase: event.phase, items: event.items, hasMore: event.hasMore, sources: 1, timedOut: event.error === 'timeout' };
  const groups = [...state.groups.filter((group) => group.key !== event.key), merged];
  const position = (key) => {
    const index = state.order.indexOf(key);
    return index < 0 ? Number.MAX_SAFE_INTEGER : index;
  };
  groups.sort((a, b) => position(a.key) - position(b.key));
  return { ...state, groups };
}

function applyEvent(state, event) {
  if (event.kind === 'meta') {
    return { ...state, query: event.query, order: event.groups.map((group) => group.key), deepPlanned: event.groups.some((group) => group.phase === 'deep') };
  }
  if (event.kind === 'group') return addGroup(state, event);
  if (event.kind === 'done') return { ...state, results: event.results, ms: event.ms };
  if (event.kind === 'error') return { ...state, status: 'error' };
  return state;
}

async function readEvents(response, onEvent) {
  const handle = (line) => {
    const text = line.trim();
    if (!text) return;
    try {
      onEvent(JSON.parse(text));
    } catch {
      // a broken line is skipped, the rest of the stream still counts
    }
  };
  if (!response.body || !response.body.getReader) {
    (await response.text()).split('\n').forEach(handle);
    return;
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let newline = buffer.indexOf('\n');
    while (newline >= 0) {
      handle(buffer.slice(0, newline));
      buffer = buffer.slice(newline + 1);
      newline = buffer.indexOf('\n');
    }
  }
  handle(buffer + decoder.decode());
}

/*
  text: what to search; types: server types (null = everything); limit: results per group; enabled: false for the
  pages & actions filter. Returns { status: idle | loading | done | error | expired, groups, query, ms, results, stale }.
*/
export function useSearchStream({ text, types, limit, enabled, delay = 140 }) {
  const [state, setState] = useState(EMPTY_SEARCH);
  const typesKey = types ? types.join(',') : '';

  useEffect(() => {
    const trimmed = String(text || '').trim();
    if (!enabled || trimmed.length < 2) {
      setState(EMPTY_SEARCH);
      return undefined;
    }
    const params = new URLSearchParams({ q: trimmed });
    if (typesKey) params.set('types', typesKey);
    if (limit) params.set('limit', String(limit));
    const key = params.toString();
    const cached = cacheGet(key);
    if (cached) {
      setState(cached);
      return undefined;
    }

    const controller = new AbortController();
    let draft = null;
    let flushTimer = null;
    const flush = () => {
      flushTimer = null;
      if (draft) setState(draft);
    };
    const scheduleFlush = () => {
      if (flushTimer === null) flushTimer = window.setTimeout(flush, FLUSH_MS);
    };

    setState((previous) => (previous.status === 'idle' ? { ...EMPTY_SEARCH, status: 'loading', text: trimmed } : { ...previous, status: 'loading', stale: true }));

    const startTimer = window.setTimeout(async () => {
      const started = Date.now();
      draft = { ...EMPTY_SEARCH, status: 'loading', text: trimmed };
      try {
        const response = await fetch(`/api/search/stream?${key}`, { signal: controller.signal, cache: 'no-store', headers: { Accept: 'application/x-ndjson' } });
        if (response.status === 401) {
          draft = { ...draft, status: 'expired' };
          flush();
          return;
        }
        if (!response.ok) throw new Error(`search failed with ${response.status}`);
        await readEvents(response, (event) => {
          draft = applyEvent(draft, event);
          // the meta event alone would only empty the list: wait for the first results
          if (event.kind === 'group') scheduleFlush();
        });
        if (flushTimer !== null) window.clearTimeout(flushTimer);
        draft = { ...draft, status: draft.status === 'error' ? 'error' : 'done', ms: draft.ms ?? Date.now() - started };
        flush();
        if (draft.status === 'done') cachePut(key, draft);
      } catch (error) {
        if (error.name === 'AbortError') return;
        draft = { ...(draft || EMPTY_SEARCH), status: 'error', stale: false };
        flush();
      }
    }, delay);

    return () => {
      window.clearTimeout(startTimer);
      if (flushTimer !== null) window.clearTimeout(flushTimer);
      controller.abort();
    };
  }, [text, typesKey, limit, enabled, delay]);

  return state;
}
